import { apiClient } from 'axios-client';
import { usePublishReview } from 'generated/api-client/@tanstack/react-query.gen';
import { useCurrentBilikPerson } from 'hooks/use-current-bilik-person/use-current-bilik-person';
import { useReviewLocker } from 'modules/review/hooks/useReviewLocker';
import { Review } from 'modules/review/types/review.type';
import React, { FunctionComponent, useCallback, useState } from 'react';
import { Button, Form, Icon, Modal, Radio } from 'semantic-ui-react';
import { ToastSuccess, ToastWarn } from 'utils';

type PublishReviewButtonProps = {
  review: Review;
  onReviewChange: () => void;
  disabled?: boolean;
};

export const PublishReviewButton: FunctionComponent<
  PublishReviewButtonProps
> = ({ review, onReviewChange, disabled = false }) => {
  const reviewId = review.id;
  // Publishing a checked review ends the verification flow: the proof
  // provider is asked and the check-end endpoint is called first.
  const isCheckEnd = review.checker !== null;

  // When the pro reply is still pending, publishing keeps the lock: it is
  // released once the reply itself gets moderated.
  const hasPendingReply = review.replyStatus === 'draft';

  const { currentBilikPerson } = useCurrentBilikPerson();

  const { unlock, checkLocker } = useReviewLocker(reviewId);

  const { mutateAsync: publish } = usePublishReview();

  const [isPublishing, setIsPublishing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [proofProvider, setProofProvider] = useState<string>();

  const publishReview = useCallback(
    async (proofProvider?: string) => {
      const locker = await checkLocker();
      if (locker && locker.id !== currentBilikPerson?.id) {
        return {
          success: false,
          message: `Avis verrouillé par ${locker.givenName} ${locker.familyName}, impossible de le valider.`,
        };
      }

      if (!hasPendingReply) {
        await unlock();
      }

      if (isCheckEnd) {
        const response = await apiClient.post(`/form/review-pro/check-end`, {
          proReviewId: reviewId,
          proofProvider,
        });

        if (!response.data.success) {
          return {
            success: false,
            message: response.data.message,
          };
        }
      }

      // V2
      await publish({ path: { reviewId } });

      // Send emails
      const response = await apiClient.post(`/form/review-pro/publish`, {
        proReviewId: reviewId,
      });

      return {
        success: response.data.success,
        message: response.data.message,
      };
    },
    [
      reviewId,
      isCheckEnd,
      currentBilikPerson?.id,
      publish,
      hasPendingReply,
      unlock,
      checkLocker,
    ],
  );

  const handlePublish = useCallback(
    async (proofProvider?: string) => {
      setIsPublishing(true);
      const response = await publishReview(proofProvider);
      response.success
        ? ToastSuccess('Succès', response.message)
        : ToastWarn('Attention', response.message);
      setIsPublishing(false);
      onReviewChange();
    },
    [publishReview, onReviewChange],
  );

  return (
    <>
      <Button
        basic
        positive
        disabled={isPublishing || disabled}
        loading={isPublishing}
        onClick={(): void => {
          if (isCheckEnd) {
            setIsModalOpen(true);
          } else {
            handlePublish();
          }
        }}
      >
        <Icon name="checkmark" />
        Valider
      </Button>
      <Modal
        open={isModalOpen}
        onClose={(): void => {
          setIsModalOpen(false);
        }}
        closeOnDimmerClick={false}
        closeOnEscape={false}
        closeIcon
      >
        <Modal.Header>Qui a transmis le devis / facture ?</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <Radio
                label="Le particulier"
                value="pi"
                name="proofProvider"
                checked={proofProvider === 'pi'}
                onChange={(): void => {
                  setIsModalOpen(true);
                  setProofProvider('pi');
                }}
              />
            </Form.Field>
            <Form.Field>
              <Radio
                label="Le pro"
                value="pro"
                name="proofProvider"
                checked={proofProvider === 'pro'}
                onChange={(): void => {
                  setIsModalOpen(true);
                  setProofProvider('pro');
                }}
              />
            </Form.Field>
            <Form.Field>
              <Radio
                label="Le particulier et le pro"
                value="proAndPi"
                name="proofProvider"
                checked={proofProvider === 'proAndPi'}
                onChange={(): void => {
                  setIsModalOpen(true);
                  setProofProvider('proAndPi');
                }}
              />{' '}
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={(): void => {
              setIsModalOpen(false);
            }}
          >
            Annuler
          </Button>
          <Button
            primary
            loading={isPublishing}
            disabled={proofProvider === undefined}
            onClick={async (): Promise<void> => {
              await handlePublish(proofProvider);
              setIsModalOpen(false);
            }}
          >
            <Icon name="envelope" />
            Envoyer
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};
